/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import ajax from 'embercom/lib/ajax';
import abbreviatedRelativeTime from 'embercom/lib/abbreviated-relative-time';
import Formatters from 'embercom/lib/reporting/flexible/formatters';
import { isNumber } from 'underscore';
import { tracked } from '@glimmer/tracking';
import type Session from './session';
import type Inbox from 'embercom/objects/inbox/inboxes/inbox';
import { type TaskFunction } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { InboxCategory } from 'embercom/models/data/inbox/inbox-categories';
import type View from 'embercom/objects/inbox/inboxes/view';
import type IntlService from 'embercom/services/intl';
// @ts-ignore - notifications-service is a classic Ember service without TypeScript types
import type NotificationsService from 'embercom/services/notifications-service';

/**
 * Type definition for raw aggregations response from backend.
 * During ES rate limiting, the backend may return an empty object {}.
 */
interface AggregationsResponse {
  aggregations?: {
    open?: number;
    unassigned?: number;
    snoozed?: number;
    waiting_for_first_reply?: number;
    csat?: number;
    closed?: number;
    idle?: number;
    avg_wait_time?: number;
    sla?: number;
  };
}

interface Settings {
  timeframeInHours: number;
  aggregationType: string;
  idleThreshold: number;
  excludeBotTime: boolean;
  excludedQueryTypes: string[];
}

export interface ConversationsAggregations {
  avgWaitTime: string;
  closed: number | null;
  csat: string | number;
  idlePercentage: number | null;
  open: number | null;
  sla: number | null;
  snoozed: number | null;
  unassigned: number | null;
  waitingForFirstReply: number | null;
}

export interface CallsAggregations {
  callsInQueue?: number;
  callsActive?: number;
  callsCompleted?: number;
  voicemailCalls?: number;
  callsAbandoned?: number;
  callsAnswerTime?: string;
  callsDuration?: string;
  phoneAvailableTeammates?: number;
  callbacksAnswered?: number;
  callbacksUnanswered?: number;
  callbacksPending?: number;
  callbacksClosed?: number;
}

export interface CallsMetrics {
  callsActive?: number;
  callsInQueue?: number;
  callsCompleted?: number;
  callsAbandoned?: number;
  voicemailCalls?: number;
  callsTeamQueueTime?: string;
  callsAnswerTime?: string;
  callsTeamTalkTime?: string;
  phoneAvailableTeammates?: number;
  callbacksAnswered?: number;
  callbacksUnanswered?: number;
  callbacksPending?: number;
  callbacksClosed?: number;
}

type CachedOverviewItem = { inboxId: string; csat: number; missCount: number; missRate: number };
type CachedOverview = [CachedOverviewItem];

export default class Inbox2OverviewService extends Service {
  @service declare session: Session;
  // @ts-ignore - notifications-service is a classic Ember service without full TypeScript types
  @service declare notificationsService: NotificationsService;
  @service declare intl: IntlService;
  @tracked isFetchingData = false;
  declare forceRefresh: TaskFunction<void, any>;

  async getInboxData(
    inboxes: Inbox[],
    settings: Settings,
    { cachedOverview }: { cachedOverview: CachedOverview },
  ) {
    try {
      this.isFetchingData = true;
      let response = await this.fetch(inboxes, settings);
      return {
        overview: this.aggregatePerInbox(
          response,
          inboxes,
          settings.excludedQueryTypes,
          cachedOverview,
        ),
        aggregations: this.readConversationsAggregations(response),
        callsAggregations: this.readCallsAggregations(response),
      };
    } finally {
      this.isFetchingData = false;
    }
  }

  async getMetricData(settings: any) {
    try {
      this.isFetchingData = true;
      let response = await this.fetchMetricDrilldown(settings);
      return response;
    } finally {
      this.isFetchingData = false;
    }
  }

  // TODO: create a type for response
  aggregatePerInbox(response: any, inboxes: Inbox[], excluded: string[], cache: CachedOverview) {
    return inboxes.map((inbox) => this.readResponseForInbox(inbox, response, excluded, cache));
  }

  readResponseForInbox(inbox: Inbox, response: any, excluded: string[], cache: CachedOverview) {
    let readFromCache = (key: keyof CachedOverviewItem) =>
      cache.find(({ inboxId }) => inboxId === inbox.id)?.[key];

    let slaData = excluded.includes('sla')
      ? { missRate: readFromCache('missRate'), missCount: readFromCache('missCount') }
      : this.getSlaData(response, inbox);

    let callsData: CallsMetrics = {};

    if (this.session.workspace.isFeatureEnabled('inbound-phone-call')) {
      callsData = {
        callsActive: this.getCallsActive(response, inbox),
        callsInQueue: this.getCallsInQueue(response, inbox),
        callsCompleted: this.getCallsCompleted(response, inbox),
        callsAbandoned: this.getCallsAbandoned(response, inbox),
        voicemailCalls: this.getVoicemailCalls(response, inbox),
        callsTeamQueueTime: this.getCallsTeamQueueTime(response, inbox),
        callsAnswerTime: this.getCallsAnswerTimeForInbox(response, inbox),
        callsTeamTalkTime: this.getCallsTeamTalkTime(response, inbox),
        phoneAvailableTeammates: this.getPhoneAvailableTeammates(response, inbox),
        callbacksAnswered: this.getCallbacksAnswered(response, inbox),
        callbacksUnanswered: this.getCallbacksUnanswered(response, inbox),
        callbacksPending: this.getCallbacksPending(response, inbox),
        callbacksClosed: this.getCallbacksClosed(response, inbox),
      };
    }

    return {
      inboxId: inbox.id,
      inboxIcon: this.getIcon(inbox),
      name: this.getName(inbox),
      open: this.getOpen(response, inbox),
      unassigned: this.getUnassigned(response, inbox),
      waitingForReply: this.getWaitingForReply(response, inbox),
      longestWaiting: this.getLongestWaiting(response, inbox),
      longestWaitingTimestamp: this.getLongestWaitingTimestamp(response, inbox),
      closed: this.getClosed(response, inbox),
      avgWaitTime: this.getAvgWaitTime(response.avg_wait_time[inbox.id]),
      avgWaitTimeInSeconds: response.avg_wait_time[inbox.id],
      csat: excluded.includes('csat')
        ? readFromCache('csat')
        : this.getCsat(response.csat[inbox.id]),
      activeTeammates: this.getActiveTeammates(response, inbox),
      idle: this.getIdle(response, inbox),
      snoozed: this.getSnoozed(response, inbox),
      ...slaData,
      ...callsData,
    };
  }

  readConversationsAggregations(response: AggregationsResponse): ConversationsAggregations | null {
    // Handle empty aggregations object returned during ES rate limiting (429 errors)
    // Detect degraded mode: backend returned empty aggregations due to ES rate limiting
    let isDegradedMode = !response.aggregations || Object.keys(response.aggregations).length === 0;

    if (isDegradedMode) {
      // Show error banner and skip metrics update entirely
      // Return null to signal caller to keep showing previous/cached data
      this.notificationsService.notifyError(
        this.intl.t('inbox.dashboard.metrics-reload-failed'),
        undefined,
        'inbox-metrics-failed',
      );
      return null;
    }

    let aggregations = response.aggregations!;

    return {
      open: aggregations.open ?? null,
      unassigned: aggregations.unassigned ?? null,
      snoozed: aggregations.snoozed ?? null,
      waitingForFirstReply: aggregations.waiting_for_first_reply ?? null,
      csat: this.getCsat(aggregations.csat),
      closed: aggregations.closed ?? null,
      idlePercentage: this.getIdlePercentage(response),
      avgWaitTime: this.getAvgWaitTime(aggregations.avg_wait_time),
      sla: aggregations.sla ?? null,
    };
  }

  readCallsAggregations(response: any): CallsAggregations {
    return {
      callsInQueue: response.calls_aggregations?.calls_in_queue,
      callsActive: response.calls_aggregations?.calls_active,
      callsCompleted: response.calls_aggregations?.calls_completed,
      voicemailCalls: response.calls_aggregations?.voicemail_calls,
      callsAbandoned: response.calls_aggregations?.calls_abandoned,
      callsAnswerTime: this.getCallsAnswerTime(response.calls_aggregations?.calls_answer_time),
      callsDuration: this.getCallsDuration(response.calls_aggregations?.calls_duration),
      phoneAvailableTeammates: response.calls_aggregations?.phone_available_teammates,
      callbacksAnswered: response.calls_aggregations?.callbacks_answered,
      callbacksUnanswered: response.calls_aggregations?.callbacks_unanswered,
      callbacksPending: response.calls_aggregations?.callbacks_pending,
      callbacksClosed: response.calls_aggregations?.callbacks_closed,
    };
  }

  getIcon(inbox: Inbox) {
    return inbox.category === InboxCategory.View ? (inbox as View).viewSummary.icon : inbox.icon;
  }

  getName(inbox: Inbox) {
    return inbox.category === InboxCategory.View ? (inbox as View).viewSummary.name : inbox.name;
  }

  getOpen(response: any, { id }: any) {
    return response.open[id] || 0;
  }

  getUnassigned(response: any, { id }: any) {
    return response.unassigned[id] || 0;
  }

  getWaitingForReply(response: any, { id }: any) {
    return response.waiting_for_first_admin_reply[id] || 0;
  }

  getLongestWaiting(response: any, { id }: any) {
    let longestWaiting = response.longest_waiting[id];
    return {
      conversationId: longestWaiting?.['conversation_id'],
      waitingSince: longestWaiting?.['waiting_since']
        ? abbreviatedRelativeTime(longestWaiting['waiting_since'])
        : '',
    };
  }

  getLongestWaitingTimestamp(response: any, { id }: any): number | undefined {
    let longestWaiting = response.longest_waiting[id];
    if (longestWaiting?.['waiting_since']) {
      return new Date(longestWaiting?.['waiting_since']).getTime();
    }

    return undefined;
  }

  getAvgWaitTime(avgWaitTime: any) {
    return this.getFormattedTime(avgWaitTime);
  }

  getCallsDuration(callsDuration?: number) {
    return this.getFormattedTime(callsDuration);
  }

  getCallsAnswerTime(callsAnswerTime?: number) {
    return this.getFormattedTime(callsAnswerTime);
  }

  getCallsTeamQueueTime(response: any, { id }: any) {
    let callsTeamQueueTime = response.calls_team_queue_time[id];
    return this.getFormattedTime(callsTeamQueueTime);
  }

  getCallsAnswerTimeForInbox(response: any, { id }: any) {
    let callsAnswerTime = response.calls_answer_time[id];
    return this.getFormattedTime(callsAnswerTime);
  }

  getCallsTeamTalkTime(response: any, { id }: any) {
    let callsTeamTalkTime = response.calls_team_talk_time[id];
    return this.getFormattedTime(callsTeamTalkTime);
  }

  getFormattedTime(timeInSeconds: any) {
    if (timeInSeconds) {
      let timeFormatter = new Formatters.seconds(); // eslint-disable-line new-cap
      return timeFormatter.formatCounter(timeInSeconds);
    }

    return '';
  }

  getClosed(response: any, { id }: any) {
    return response.closed[id] || 0;
  }

  getCsat(csat: any) {
    return isNumber(csat) ? Math.round(csat) : '';
  }

  getActiveTeammates(response: any, { id }: any) {
    return response.active_teammates[id];
  }

  getPhoneAvailableTeammates(response: any, { id }: any) {
    return response.phone_available_teammates[id];
  }

  getIdle(response: any, { id }: any) {
    return response.idle?.[id] || 0;
  }

  getIdlePercentage(response: AggregationsResponse): number | null {
    let aggregations = response.aggregations!;
    let totalOpen = aggregations.open;
    let idle = aggregations.idle;

    // Return null if individual fields are missing (they're optional) or totalOpen is 0
    if (totalOpen == null || idle == null || totalOpen === 0) {
      return null;
    }

    return Math.round((idle * 100) / totalOpen);
  }

  getSnoozed(response: any, { id }: any) {
    return response.snoozed[id] || 0;
  }

  getSlaData(response: any, { id }: any) {
    if (!response.sla[id]) {
      return null;
    }

    let conversationsWithSla = response.sla[id].conversations_with_sla;
    let missed = response.sla[id].missed;
    return {
      missRate:
        conversationsWithSla === 0 ? null : Math.round((missed * 100) / conversationsWithSla),
      missCount: missed,
    };
  }

  getCallsActive(response: any, { id }: any) {
    return response.calls_active[id] || 0;
  }

  getCallsInQueue(response: any, { id }: any) {
    return response.calls_in_queue[id] || 0;
  }

  getCallsCompleted(response: any, { id }: any) {
    return response.calls_completed[id] || 0;
  }

  getCallsAbandoned(response: any, { id }: any) {
    return response.calls_abandoned[id] || 0;
  }

  getCallbacksAnswered(response: any, { id }: any) {
    return response.callbacks_answered[id] || 0;
  }

  getCallbacksUnanswered(response: any, { id }: any) {
    return response.callbacks_unanswered[id] || 0;
  }
  getCallbacksPending(response: any, { id }: any) {
    return response.callbacks_pending[id] || 0;
  }

  getCallbacksClosed(response: any, { id }: any) {
    return response.callbacks_closed[id] || 0;
  }

  getVoicemailCalls(response: any, { id }: any) {
    return response.voicemail_calls[id] || 0;
  }

  // set type
  fetchMetricDrilldown({
    inboxId,
    metricType,
    adminId,
    timeframeInHours,
    filter,
    aggregationType,
    idleThreshold,
    excludeBotTime,
  }: any): Promise<any> {
    return ajax({
      url: adminId
        ? '/ember/monitoring/teammate_activities/metric_drilldown'
        : '/ember/monitoring/inbox_overview/metric_drilldown',
      type: 'GET',
      data: {
        app_id: this.session.workspace.id,
        inbox_identifier: inboxId,
        metric_type: metricType,
        admin_id: adminId,
        timeframe: timeframeInHours * 60,
        filter,
        aggregation_type: aggregationType,
        idle_threshold: idleThreshold,
        exclude_bot_time: excludeBotTime,
      },
    });
  }

  fetch(
    inboxes: Inbox[],
    {
      timeframeInHours,
      aggregationType,
      idleThreshold,
      excludeBotTime,
      excludedQueryTypes,
    }: Settings,
  ): Promise<any> {
    return ajax({
      url: '/ember/monitoring/inbox_overview',
      type: 'GET',
      data: {
        app_id: this.session.workspace.id,
        inbox_identifiers: inboxes.map(({ id }) => id),
        timeframe: timeframeInHours * 60,
        aggregation_type: aggregationType,
        idle_threshold: idleThreshold,
        exclude_bot_time: excludeBotTime,
        excluded_query_types: excludedQueryTypes,
      },
    });
  }

  setForceRefresh(refreshTask: any) {
    this.forceRefresh = refreshTask;
  }

  doForceRefresh(options: any) {
    if (this.forceRefresh) {
      taskFor(this.forceRefresh).perform(options);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    inbox2OverviewService: Inbox2OverviewService;
    'inbox2-overview-service': Inbox2OverviewService;
  }
}
